/**
 * Phase 4 §3 — formation library drawer.
 *
 * Slide-up panel anchored above the tactics HUD. Two tabs:
 *   - Standard: the union of the seed FORMATIONS list (from
 *     `formationProjection.SUPPORTED_FORMATIONS`) and the new
 *     EXTENDED_FORMATIONS codes, deduped on the formation code.
 *   - Custom: the coach's own saved formations from localStorage
 *     (`listCustomFormations()`), each with Apply + Delete affordances.
 *
 * Apply fires `onApply(codeOrId, team)`. The team defaults to the
 * `currentTeam` prop or 'home' when not specified — the caller (the
 * tactics page HUD) is expected to know which team it's editing.
 *
 * Closing rules:
 *   - Escape key fires `onClose`.
 *   - The header × button fires `onClose`.
 *   - role="dialog" + aria-modal="true" so screen readers know it's a
 *     modal layer over the underlying canvas.
 */

import { useEffect, useMemo, useState } from 'react';
import { SUPPORTED_FORMATIONS } from '../tactics3d/formationProjection';
import { EXTENDED_FORMATIONS } from '@4cm/core/tactics/formationsExtended';
import {
  deleteCustomFormation,
  listCustomFormations,
} from '../../lib/customFormationsStore';
import type { TacticsCustomFormation } from '@4cm/core/tactics/formationsExtended';
import { FORMATIONS as SEED_FORMATIONS } from '@4cm/core/constants';
import './FormationLibraryDrawer.css';

export interface FormationLibraryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the coach taps Apply on a standard code or a custom id. */
  onApply: (formationCodeOrCustomId: string, team: 'home' | 'away') => void;
  /** Which team is being edited — drives the Apply target. Defaults to 'home'. */
  currentTeam?: 'home' | 'away';
}

type Tab = 'standard' | 'custom';

interface StandardEntry {
  code: string;
  /** Slot-shape summary string, e.g. "GK + 4 + 3 + 3". */
  summary: string;
}

/**
 * Build the "GK + back + mid + fwd" summary from the canonical world-
 * metres slots. Falls back to a flat slot count if the role mix isn't
 * a recognised back/mid/fwd split.
 */
function buildSummary(slots: { role: string }[]): string {
  let gk = 0;
  let def = 0;
  let mid = 0;
  let fwd = 0;
  const D = new Set(['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW']);
  const M = new Set(['CM', 'CDM', 'CAM', 'LM', 'RM', 'DM', 'AM', 'M']);
  const F = new Set(['ST', 'CF', 'LW', 'RW', 'LF', 'RF', 'SS', 'LS', 'RS', 'F']);
  for (const s of slots) {
    const r = s.role.toUpperCase();
    if (r === 'GK') gk += 1;
    else if (D.has(r)) def += 1;
    else if (M.has(r)) mid += 1;
    else if (F.has(r)) fwd += 1;
  }
  if (gk + def + mid + fwd !== slots.length) {
    return `${slots.length} slots`;
  }
  return `GK + ${def} + ${mid} + ${fwd}`;
}

/**
 * Compute the standard-tab list. Combines the seed 11v11 formations
 * (from constants.FORMATIONS, filtered via SUPPORTED_FORMATIONS) and
 * the EXTENDED_FORMATIONS codes, deduped by code. Seed wins on
 * duplicates because callers already wire its positions through the
 * existing projection path.
 */
function buildStandardList(): StandardEntry[] {
  const seen = new Set<string>();
  const out: StandardEntry[] = [];

  for (const code of SUPPORTED_FORMATIONS) {
    if (seen.has(code)) continue;
    seen.add(code);
    const positions = SEED_FORMATIONS[code]?.positions ?? [];
    out.push({ code, summary: buildSummary(positions) });
  }

  for (const code of Object.keys(EXTENDED_FORMATIONS)) {
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({ code, summary: buildSummary(EXTENDED_FORMATIONS[code].slots) });
  }

  return out;
}

export function FormationLibraryDrawer({
  open,
  onClose,
  onApply,
  currentTeam,
}: FormationLibraryDrawerProps) {
  const [tab, setTab] = useState<Tab>('standard');
  const [customs, setCustoms] = useState<TacticsCustomFormation[]>([]);

  // Re-read storage every time the drawer opens so we pick up entries
  // saved by the Brain ("save this as Tuesday press") between sessions.
  useEffect(() => {
    if (open) setCustoms(listCustomFormations());
  }, [open]);

  // Escape closes — registered only while open so we don't intercept
  // global keystrokes on idle pages.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const standard = useMemo(() => buildStandardList(), []);

  if (!open) return null;

  const team: 'home' | 'away' = currentTeam ?? 'home';

  const handleDelete = (id: string) => {
    deleteCustomFormation(id);
    setCustoms(listCustomFormations());
  };

  return (
    <div
      className="formation-library-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Formation library"
    >
      <div className="formation-library-drawer__header">
        <h2 className="formation-library-drawer__title">Formations</h2>
        <button
          type="button"
          className="formation-library-drawer__close"
          onClick={onClose}
          aria-label="Close formation library"
        >
          ×
        </button>
      </div>

      <div className="formation-library-drawer__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'standard'}
          className="formation-library-drawer__tab"
          data-active={tab === 'standard' || undefined}
          onClick={() => setTab('standard')}
        >
          Standard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'custom'}
          className="formation-library-drawer__tab"
          data-active={tab === 'custom' || undefined}
          onClick={() => setTab('custom')}
        >
          Custom
        </button>
      </div>

      {tab === 'standard' && (
        <div className="formation-library-drawer__list" role="list">
          {standard.map((entry) => (
            <div className="formation-library-drawer__row" role="listitem" key={entry.code}>
              <div className="formation-library-drawer__row-body">
                <div className="formation-library-drawer__row-title">{entry.code}</div>
                <div className="formation-library-drawer__row-meta">{entry.summary}</div>
              </div>
              <button
                type="button"
                className="formation-library-drawer__apply"
                onClick={() => onApply(entry.code, team)}
                aria-label={`Apply formation ${entry.code}`}
              >
                Apply
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'custom' && (
        <div className="formation-library-drawer__list" role="list">
          {customs.length === 0 && (
            <div className="formation-library-drawer__empty">
              No custom formations yet. Build a layout, then save it via the Brain or the
              formation menu to see it here.
            </div>
          )}
          {customs.map((cf) => (
            <div className="formation-library-drawer__row" role="listitem" key={cf.id}>
              <div className="formation-library-drawer__row-body">
                <div className="formation-library-drawer__row-title">{cf.name}</div>
                <div className="formation-library-drawer__row-meta">
                  {buildSummary(cf.slots)}
                </div>
              </div>
              <button
                type="button"
                className="formation-library-drawer__apply"
                onClick={() => onApply(cf.id, team)}
                aria-label={`Apply custom formation ${cf.name}`}
              >
                Apply
              </button>
              <button
                type="button"
                className="formation-library-drawer__delete"
                onClick={() => handleDelete(cf.id)}
                aria-label={`Delete custom formation ${cf.name}`}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
